import React, { useState, useEffect, useCallback, useMemo } from 'react'
import firebase from 'firebase'
import { FBREF_COURSE_EVALUATIONS } from '@app/evaluation-core/refs'
import { Eval } from '@app/evaluation-core/types'
import { useClubData } from '@app/courses/hooks'
import {
  SearchOverviewProps,
  ListItem,
  ClubData,
  CourseData,
  ClubItemProps,
  CourseItemProps,
} from './types'
import { useEvaluationFilters } from './useEvaluationFilters'

import * as S from './styled'

const SearchOverview: React.FC = () => {
  const [items, setItems] = useState<ListItem[]>([])
  const [groupedData, setGroupedData] = useState<ClubData[]>([])
  const [loadingEvals, setLoadingEvals] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedClubs, setExpandedClubs] = useState<Set<string>>(new Set())
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set())
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  const { clubsWithCourses } = useClubData()

  const {
    searchTerm,
    setSearchTerm,
    selectedDistricts,
    handleDistrictSelect,
    selectedRegions,
    handleRegionSelect,
    selectedEvalStatuses,
    handleEvalStatusSelect,
    selectedFinalizedYears,
    handleFinalizedYearSelect,
    selectedEvaluationYears,
    handleEvaluationYearSelect,
    selectedManualVersions,
    handleManualVersionSelect,
    clearFilters,
    filteredGroupedData,
  } = useEvaluationFilters({ groupedData })

  const fetchEvaluations = useCallback(async () => {
    setLoadingEvals(true)
    setError(null)

    try {
      const snapshot = await firebase
        .database()
        .ref(FBREF_COURSE_EVALUATIONS())
        .get()

      if (snapshot.exists()) {
        const data = snapshot.val() as Record<string, Eval>
        const evalsMap = new Map<string, Eval>()

        Object.entries(data).forEach(([key, value]) => {
          evalsMap.set(key, value)
        })

        groupEvaluationsByClubAndCourse(evalsMap)
      } else {
        setError('No evaluations found')
      }
    } catch (err) {
      console.error('Error fetching evaluations:', err)
      setError('Failed to fetch evaluations')
    } finally {
      setLoadingEvals(false)
    }
  }, [])

  useEffect(() => {
    console.log('fetching evaluations')
    fetchEvaluations()
  }, [fetchEvaluations])

  const groupEvaluationsByClubAndCourse = useCallback(
    (evalsMap: Map<string, Eval>) => {
      const clubByIdMap = new Map<string, ClubData>()
      const clubByNameMap = new Map<string, ClubData>()

      const UNGROUPED_CLUB_NAME = 'UNGROUPED'
      const UNGROUPED_COURSE_ID = 'ungrouped-course'
      const UNGROUPED_COURSE_NAME = 'Ungrouped Ratings'

      const ungroupedClub: ClubData = {
        clubName: UNGROUPED_CLUB_NAME,
        courses: [
          {
            courseId: UNGROUPED_COURSE_ID,
            courseName: UNGROUPED_COURSE_NAME,
            evaluations: [],
          },
        ],
      }
      clubByNameMap.set(UNGROUPED_CLUB_NAME.toLowerCase(), ungroupedClub)

      const normalizeName = (name: string): string => name.toLowerCase().trim()

      evalsMap.forEach((evaluation) => {
        if (!evaluation.clubId && !evaluation.clubName) {
          const ungroupedCourse = clubByNameMap.get(
            UNGROUPED_CLUB_NAME.toLowerCase()
          )!.courses[0]
          ungroupedCourse.evaluations.push(evaluation)
          return
        }

        let club: ClubData | undefined

        if (evaluation.clubId && clubByIdMap.has(String(evaluation.clubId))) {
          club = clubByIdMap.get(String(evaluation.clubId))
        } else if (evaluation.clubName) {
          const normalizedClubName = normalizeName(evaluation.clubName)
          if (clubByNameMap.has(normalizedClubName)) {
            club = clubByNameMap.get(normalizedClubName)
          }
        }

        if (!club) {
          club = {
            clubId: evaluation.clubId ? String(evaluation.clubId) : undefined,
            clubName: evaluation.clubName || `Club ID: ${evaluation.clubId}`,
            courses: [],
          }

          if (evaluation.clubId) {
            clubByIdMap.set(String(evaluation.clubId), club)
          }
          if (evaluation.clubName) {
            clubByNameMap.set(normalizeName(evaluation.clubName), club)
          }
        } else {
          if (evaluation.clubId && !club.clubId) {
            club.clubId = String(evaluation.clubId)
            clubByIdMap.set(String(evaluation.clubId), club)
          }
          if (evaluation.clubName && club.clubName.startsWith('Club ID:')) {
            club.clubName = evaluation.clubName
            clubByNameMap.set(normalizeName(evaluation.clubName), club)
          }
        }

        if (!club) {
          console.error(
            'Failed to create or find club for evaluation',
            evaluation
          )
          return
        }

        if (!evaluation.courseId && !evaluation.courseName) {
          const ungroupedCourseId = `${club.clubName}-ungrouped`
          let ungroupedCourse = club.courses.find(
            (c) =>
              c.courseId === ungroupedCourseId ||
              normalizeName(c.courseName) === normalizeName('Ungrouped Ratings')
          )

          if (!ungroupedCourse) {
            ungroupedCourse = {
              courseId: ungroupedCourseId,
              courseName: 'Ungrouped Ratings',
              evaluations: [],
            }
            club.courses.push(ungroupedCourse)
          }

          ungroupedCourse.evaluations.push(evaluation)
          return
        }

        let course: CourseData | undefined

        if (evaluation.courseId) {
          course = club.courses.find(
            (c) => c.courseId === String(evaluation.courseId)
          )
        }

        if (!course && evaluation.courseName) {
          const normalizedCourseName = normalizeName(evaluation.courseName)
          course = club.courses.find(
            (c) => normalizeName(c.courseName) === normalizedCourseName
          )
        }

        if (!course) {
          course = {
            courseId: evaluation.courseId
              ? String(evaluation.courseId)
              : `${club.clubName}-${evaluation.courseName}`,
            courseName:
              evaluation.courseName || `Course ID: ${evaluation.courseId}`,
            evaluations: [],
          }
          club.courses.push(course)
        } else {
          if (evaluation.courseId && !course.courseId.includes('-')) {
            course.courseId = String(evaluation.courseId)
          }
          if (
            evaluation.courseName &&
            course.courseName.startsWith('Course ID:')
          ) {
            course.courseName = evaluation.courseName
          }
        }

        course.evaluations.push(evaluation)
      })

      if (ungroupedClub.courses[0].evaluations.length === 0) {
        clubByNameMap.delete(UNGROUPED_CLUB_NAME.toLowerCase())
      }

      const uniqueClubs = new Map<string, ClubData>()

      clubByIdMap.forEach((club) => {
        const key = club.clubId || normalizeName(club.clubName)
        uniqueClubs.set(key, club)
      })

      clubByNameMap.forEach((club) => {
        const key = club.clubId || normalizeName(club.clubName)
        if (!uniqueClubs.has(key)) {
          uniqueClubs.set(key, club)
        }
      })

      const groupedArray = Array.from(uniqueClubs.values())

      groupedArray.sort((a, b) => {
        if (a.clubName === UNGROUPED_CLUB_NAME) return 1
        if (b.clubName === UNGROUPED_CLUB_NAME) return -1
        return a.clubName.localeCompare(b.clubName)
      })

      if (clubsWithCourses) {
        groupedArray.forEach((club) => {
          const matchingClub = clubsWithCourses.find(
            (c) =>
              (club.clubId && c.id === club.clubId) ||
              normalizeName(c.name) === normalizeName(club.clubName) ||
              (c.shortName &&
                normalizeName(c.shortName) === normalizeName(club.clubName))
          )

          if (matchingClub) {
            club.districtCountry = matchingClub.districtCountry
            club.districtName = matchingClub.districtName
            club.districtRegionName = matchingClub.districtRegionName
            club.branchStatus = matchingClub.branchStatus

            const realCourses = club.courses.filter(
              (c) => !c.courseName.includes('Ungrouped Ratings')
            )

            if (
              realCourses.length === 0 &&
              matchingClub.courses &&
              matchingClub.courses.length > 0
            ) {
              matchingClub.courses.forEach((miCourse) => {
                const newCourse: CourseData = {
                  courseId: miCourse.id,
                  courseName: miCourse.name,
                  evaluations: [],
                }
                club.courses.push(newCourse)
              })
            }

            const ungroupedCourseIndex = club.courses.findIndex((c) =>
              c.courseName.includes('Ungrouped Ratings')
            )

            if (ungroupedCourseIndex !== -1) {
              const updatedRealCourses = club.courses.filter(
                (c) => !c.courseName.includes('Ungrouped Ratings')
              )

              if (updatedRealCourses.length === 1) {
                const ungroupedCourse = club.courses[ungroupedCourseIndex]
                updatedRealCourses[0].evaluations.push(
                  ...ungroupedCourse.evaluations
                )

                club.courses.splice(ungroupedCourseIndex, 1)
              }
            }
          }
        })
      }

      groupedArray.forEach((club) => {
        const uniqueCoursesByName = new Map<string, CourseData>()

        club.courses.forEach((course) => {
          const normalizedName = normalizeName(course.courseName)

          if (normalizedName.includes('ungrouped ratings')) {
            uniqueCoursesByName.set(normalizedName, course)
            return
          }
          if (uniqueCoursesByName.has(normalizedName)) {
            const existingCourse = uniqueCoursesByName.get(normalizedName)!

            if (
              !course.courseId.includes('-') &&
              existingCourse.courseId.includes('-')
            ) {
              existingCourse.courseId = course.courseId
            }
            existingCourse.evaluations.push(...course.evaluations)
          } else {
            uniqueCoursesByName.set(normalizedName, course)
          }
        })

        club.courses = Array.from(uniqueCoursesByName.values())

        club.courses.sort((a, b) => {
          if (
            normalizeName(a.courseName) === normalizeName('Ungrouped Ratings')
          )
            return 1
          if (
            normalizeName(b.courseName) === normalizeName('Ungrouped Ratings')
          )
            return -1
          return a.courseName.localeCompare(b.courseName, undefined, {
            sensitivity: 'base',
          })
        })

        club.courses.forEach((course) => {
          course.evaluations.sort((a, b) => {
            const dateA = a.evaluationDate
              ? new Date(a.evaluationDate).getTime()
              : 0
            const dateB = b.evaluationDate
              ? new Date(b.evaluationDate).getTime()
              : 0
            return dateB - dateA
          })
        })
      })

      setGroupedData(groupedArray)
    },
    [clubsWithCourses]
  )

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value)
    },
    [setSearchTerm]
  )

  const handleTagSelect = useCallback((tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }, [])

  const toggleClubExpansion = useCallback((clubName: string) => {
    setExpandedClubs((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(clubName)) {
        newSet.delete(clubName)
      } else {
        newSet.add(clubName)
      }
      return newSet
    })
  }, [])

  const toggleCourseExpansion = useCallback((courseId: string) => {
    setExpandedCourses((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(courseId)) {
        newSet.delete(courseId)
      } else {
        newSet.add(courseId)
      }
      return newSet
    })
  }, [])

  const handleEvaluationClick = useCallback((evaluation: Eval) => {
    console.log('Evaluation clicked:', evaluation)
  }, [])

  const allTags = useMemo(() => {
    return Array.from(
      new Set(
        items
          .filter((item) => item.tags && item.tags.length > 0)
          .flatMap((item) => item.tags as string[])
      )
    )
  }, [items])

  return (
    <S.Container>
      <S.SearchSection>
        <S.SearchInput
          type="text"
          placeholder="Search clubs..."
          value={searchTerm}
          onChange={handleSearch}
        />

        <S.FilterHeaderContainer>
          <S.FilterSectionTitle>Filters</S.FilterSectionTitle>
          {(selectedDistricts.length > 0 ||
            selectedRegions.length > 0 ||
            selectedEvalStatuses.length > 0 ||
            selectedFinalizedYears.length > 0 ||
            selectedEvaluationYears.length > 0 ||
            selectedManualVersions.length > 0) && (
            <S.ClearFiltersButton onClick={clearFilters}>
              Clear All Filters
            </S.ClearFiltersButton>
          )}
        </S.FilterHeaderContainer>

        <S.FiltersContainer>
          <S.FilterSection>
            <S.FilterSectionTitle>Districts</S.FilterSectionTitle>
            <S.FilterPillsContainer>
              {clubsWithCourses &&
                Array.from(
                  new Set(
                    clubsWithCourses
                      .map((club) => club.districtName)
                      .filter(Boolean)
                  )
                ).map((district) => (
                  <S.FilterPill
                    key={`district-${district}`}
                    selected={selectedDistricts.includes(district)}
                    onClick={() => handleDistrictSelect(district)}
                  >
                    {district}
                  </S.FilterPill>
                ))}
            </S.FilterPillsContainer>
          </S.FilterSection>

          <S.FilterSection>
            <S.FilterSectionTitle>Regions</S.FilterSectionTitle>
            <S.FilterPillsContainer>
              {clubsWithCourses &&
                Array.from(
                  new Set(
                    clubsWithCourses
                      .map((club) => club.districtRegionName)
                      .filter(Boolean)
                  )
                ).map((region) => (
                  <S.FilterPill
                    key={`region-${region}`}
                    selected={selectedRegions.includes(region)}
                    onClick={() => handleRegionSelect(region)}
                  >
                    {region}
                  </S.FilterPill>
                ))}
            </S.FilterPillsContainer>
          </S.FilterSection>

          <S.FilterSection>
            <S.FilterSectionTitle>Evaluation Status</S.FilterSectionTitle>
            <S.FilterPillsContainer>
              {[
                { key: 0, label: 'Not Started' },
                { key: 1, label: 'Preparing' },
                { key: 2, label: 'In Progress' },
                { key: 3, label: 'Finalized' },
              ].map((status) => (
                <S.FilterPill
                  key={`eval-status-${status.key}`}
                  selected={selectedEvalStatuses.includes(status.key)}
                  onClick={() => handleEvalStatusSelect(status.key)}
                >
                  {status.label}
                </S.FilterPill>
              ))}
            </S.FilterPillsContainer>
          </S.FilterSection>

          <S.FilterSection>
            <S.FilterSectionTitle>Finalized in Year</S.FilterSectionTitle>
            <S.FilterPillsContainer>
              {Array.from({ length: new Date().getFullYear() - 2019 }, (_, i) =>
                (2020 + i).toString()
              ).map((year) => (
                <S.FilterPill
                  key={`year-${year}`}
                  selected={selectedFinalizedYears.includes(year)}
                  onClick={() => handleFinalizedYearSelect(year)}
                >
                  {year}
                </S.FilterPill>
              ))}
            </S.FilterPillsContainer>
          </S.FilterSection>

          <S.FilterSection>
            <S.FilterSectionTitle>Rated d in Year</S.FilterSectionTitle>
            <S.FilterPillsContainer>
              {Array.from({ length: new Date().getFullYear() - 2019 }, (_, i) =>
                (2020 + i).toString()
              ).map((year) => (
                <S.FilterPill
                  key={`eval-year-${year}`}
                  selected={selectedEvaluationYears.includes(year)}
                  onClick={() => handleEvaluationYearSelect(year)}
                >
                  {year}
                </S.FilterPill>
              ))}
            </S.FilterPillsContainer>
          </S.FilterSection>

          <S.FilterSection>
            <S.FilterSectionTitle>Manual Version</S.FilterSectionTitle>
            <S.FilterPillsContainer>
              {['2020', '2024', 'Unknown'].map((version) => (
                <S.FilterPill
                  key={`manual-version-${version}`}
                  selected={selectedManualVersions.includes(version)}
                  onClick={() => handleManualVersionSelect(version)}
                >
                  {version}
                </S.FilterPill>
              ))}
            </S.FilterPillsContainer>
          </S.FilterSection>
        </S.FiltersContainer>

        <S.TagsContainer>
          {allTags.map((tag) => (
            <S.TagButton
              key={tag}
              selected={selectedTags.includes(tag)}
              onClick={() => handleTagSelect(tag)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleTagSelect(tag)
                }
              }}
            >
              {tag}
            </S.TagButton>
          ))}
        </S.TagsContainer>
      </S.SearchSection>

      <S.ListContainer>
        {loadingEvals ? (
          <S.LoadingIndicator>Loading Ratings...</S.LoadingIndicator>
        ) : error ? (
          <S.ErrorMessage>{error}</S.ErrorMessage>
        ) : filteredGroupedData.length === 0 ? (
          <S.EmptyState>
            {searchTerm ? 'No clubs match your search' : 'No clubs found'}
          </S.EmptyState>
        ) : (
          <S.HierarchicalList>
            {filteredGroupedData.map((club) => (
              <ClubItem
                key={club.clubName}
                club={club}
                isExpanded={expandedClubs.has(club.clubName)}
                onToggle={toggleClubExpansion}
                onCourseToggle={toggleCourseExpansion}
                expandedCourses={expandedCourses}
                onEvaluationClick={handleEvaluationClick}
              />
            ))}
          </S.HierarchicalList>
        )}
      </S.ListContainer>
    </S.Container>
  )
}

const ClubItem = React.memo<ClubItemProps>(
  ({
    club,
    isExpanded,
    onToggle,
    onCourseToggle,
    expandedCourses,
    onEvaluationClick,
  }) => {
    return (
      <S.StyledClubItem>
        <S.ClubHeader
          onClick={() => onToggle(club.clubName)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onToggle(club.clubName)
            }
          }}
        >
          <S.ClubHeaderMain>
            <S.ExpandIcon expanded={isExpanded}>
              {isExpanded ? '▼' : '►'}
            </S.ExpandIcon>
            <S.ClubName>{club.clubName}</S.ClubName>
            <S.CourseCount>
              {club.courses.length} course{club.courses.length !== 1 ? 's' : ''}
            </S.CourseCount>
          </S.ClubHeaderMain>
          {(club.districtCountry ||
            club.districtName ||
            club.districtRegionName ||
            club.branchStatus) && (
            <S.ClubHeaderInfo>
              {club.districtCountry && (
                <S.ClubInfoBadge>
                  <strong>Country:</strong> {club.districtCountry}
                </S.ClubInfoBadge>
              )}
              {club.districtName && (
                <S.ClubInfoBadge>
                  <strong>District:</strong> {club.districtName}
                </S.ClubInfoBadge>
              )}
              {club.districtRegionName && (
                <S.ClubInfoBadge>
                  <strong>Region:</strong> {club.districtRegionName}
                </S.ClubInfoBadge>
              )}
              {/* {club.branchStatus && (
                <S.BranchStatusBadge
                  isBranched={club.branchStatus.isBranched === 'true'}
                >
                  {club.branchStatus.isBranched === 'true'
                    ? 'Branched'
                    : 'Not Branched'}
                </S.BranchStatusBadge>
              )} */}
            </S.ClubHeaderInfo>
          )}
        </S.ClubHeader>

        {isExpanded && (
          <>
            <S.CourseList>
              {club.courses.map((course) => (
                <CourseItem
                  key={course.courseId}
                  course={course}
                  isExpanded={expandedCourses.has(course.courseId)}
                  onToggle={onCourseToggle}
                  onEvaluationClick={onEvaluationClick}
                />
              ))}
            </S.CourseList>
          </>
        )}
      </S.StyledClubItem>
    )
  }
)

const CourseItem = React.memo<CourseItemProps>(
  ({ course, isExpanded, onToggle, onEvaluationClick }) => {
    return (
      <S.StyledCourseItem>
        <S.CourseHeader
          onClick={() => onToggle(course.courseId)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onToggle(course.courseId)
            }
          }}
        >
          <S.ExpandIcon expanded={isExpanded}>
            {isExpanded ? '▼' : '►'}
          </S.ExpandIcon>
          <S.CourseName>{course.courseName}</S.CourseName>
          <S.EvaluationCount>
            {course.evaluations.length} evaluation
            {course.evaluations.length !== 1 ? 's' : ''}
          </S.EvaluationCount>
        </S.CourseHeader>

        {isExpanded && (
          <S.EvaluationList>
            {course.evaluations.map((evaluation) => (
              <S.EvaluationItem
                key={evaluation.id}
                onClick={() => onEvaluationClick(evaluation)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onEvaluationClick(evaluation)
                  }
                }}
              >
                <S.EvaluationMainContent>
                  <S.EvaluationTitle>
                    {evaluation.saveName || 'Unnamed Evaluation'}
                  </S.EvaluationTitle>
                  <S.EvaluationMetaRow>
                    <S.StatusBadge status={evaluation.status}>
                      {getStatusText(evaluation.status)}
                    </S.StatusBadge>
                    {evaluation.evaluationDate && (
                      <S.EvaluationMeta>
                        <strong>Rated:</strong>
                        {
                          new Date(evaluation.evaluationDate)
                            .toISOString()
                            .split('T')[0]
                        }
                      </S.EvaluationMeta>
                    )}
                    {evaluation.finalizedAt && (
                      <S.EvaluationMeta>
                        <strong>Finalized:</strong>
                        {
                          new Date(evaluation.finalizedAt)
                            .toISOString()
                            .split('T')[0]
                        }
                      </S.EvaluationMeta>
                    )}
                    {evaluation.manualVersion && (
                      <S.ManualVersionBadge version={evaluation.manualVersion}>
                        Manual {evaluation.manualVersion}
                      </S.ManualVersionBadge>
                    )}
                    <S.EvaluationMeta>
                      <strong>ID:</strong> {evaluation.id}
                    </S.EvaluationMeta>
                  </S.EvaluationMetaRow>
                  {evaluation.description && (
                    <S.EvaluationDescription>
                      {evaluation.description}
                    </S.EvaluationDescription>
                  )}
                </S.EvaluationMainContent>
              </S.EvaluationItem>
            ))}
          </S.EvaluationList>
        )}
      </S.StyledCourseItem>
    )
  }
)

const getStatusText = (status: number): string => {
  switch (status) {
    case 0:
      return 'Not Started'
    case 1:
      return 'Preparing'
    case 2:
      return 'In Progress'
    case 3:
      return 'Finalized'
    default:
      return 'Unknown'
  }
}

export default SearchOverview
